/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useReducer} from "react"
import {Checkbox, Col, Row, Tabs} from "antd"
import {cloneDeep, isEqual} from "lodash"
import {useTranslation} from "react-i18next"
import debounce from "debounce-promise"
import {Modules, useEdularModulesContext} from "@edular/modules"
import {KlassDropAsyncPaginate, KlassDropdown} from "components/Select"
import {BaseButton, SecondaryButton} from "components/buttons"
import {campusesService, departmentsService, generalSettingsService} from "services"
import {getFullName, handleError, toastError} from "helpers"
import {BaseDepartmentId} from "types/departments"
import {Activities} from "sections/Settings"
import {PermissionsRequired} from "components/PermissionsRequired"
import {Permissions} from "types/permission"
import {AcademicTrack} from "./../AcademicTrack"
import styles from "./GeneralSettings.module.css"
import IntegrationMapping from "sections/Settings/IntegrationMapping"
import {TabHeader} from "components/Tab"
import {FormLabel} from "components/Form"
import {useAllActivities, useAllDepartments, useCurrentProfilePermissions} from "hooks"
import {GeneralSettingField, DailyAttendanceTimeScale} from "types/settings/general-setting"
import {useMutation, useQuery} from "@tanstack/react-query"
import {YesNo} from "types/common"
import {Phase} from "../Phases/Phases"
import {Shift} from "../Shifts"
import ClockHoursTranscript from "sections/Settings/ClockHoursTranscript"
import {DailyAttendance} from "../DailyAttendance"
import {ClassroomAttendance} from "../ClassroomAttendance"
import {BaseInput, RadioButton} from "components"
import CampusSelect from "components/CampusSelect"
import cx from "classnames"
import GeofencingSettings from "sections/Settings/GeofencingSettings"

const {TabPane} = Tabs

function reducer(state, action) {
    return {...state, ...action}
}

export enum SettingsTab {
    Statuses = "statuses",
    Activities = "activities",
    LeadSources = "lead-sources",
    AcademicTrack = "academic-track",
    DailyAttendance = "daily-attendance",
    ClassroomAttendance = "classroom-attendance",
    IntegrationMapping = "integration-mapping",
    GeoFencing = "geo-fencing",
    ClockHoursTranscript = "clock-hours-transcript",
    Phases = "phases",
    Shifts = "shifts"
}

export function GeneralSettings(props) {
    const {departmentId} = props
    const {invalidateDepartmentsCache} = useAllDepartments()
    const {invalidateActivitiesCache} = useAllActivities()
    const {departments: allDepartments} = useAllDepartments()
    const {isModuleEnable} = useEdularModulesContext()
    const initialState = {
        activeTabKey: SettingsTab.Activities,
        directors: [],
        activeDirectors: [],
        oldActiveDirectors: [],
        isSubmitting: false,
        selectedProgressReport: [],
        oldSelectedProgressReport: [],
        instructionalCredit: true,
        instructionalClock: false,
        fameAdvantageEnabled: false,
        isAttendanceModuleEnabled: false,
        isPracticalAutoRejectionEnabled: false,
        practicalAutoRejectionDays: 0,
        dailyAttendanceTimeScale: DailyAttendanceTimeScale.Minutes,
        onlineCampuses: []
    }

    const currentProfilePermissions = useCurrentProfilePermissions()

    const canEditCampuses = currentProfilePermissions.hasPermissions({
        staff: [Permissions.Staff.Settings.General.Campus.Edit]
    })

    const updateGeneralSettings = useMutation(
        async (payload: Array<{field: GeneralSettingField; checked?: boolean; value?: string}>) => {
            await generalSettingsService.updateSettings(
                payload.map(({field, checked, value}) => ({
                    field,
                    value: checked ? YesNo.Yes : YesNo.No,
                    ...(value && {value})
                }))
            )
        },
        {
            onError: (error: Error) => {
                toastError(error.message)
            }
        }
    )
    const generalSettingsQuery = useQuery(
        ["general-setting"],
        async () => {
            return await generalSettingsService.getSettings([
                GeneralSettingField.ProgressReportSystemDefault,
                GeneralSettingField.ProgressReportSubmodulesBreakDown,
                GeneralSettingField.InstructionalCredit,
                GeneralSettingField.InstructionalClock,
                GeneralSettingField.FameAdvantageEnabled,
                GeneralSettingField.FameFreedomEnabled,
                GeneralSettingField.IncludeFameIdCheckInErrorLog,
                GeneralSettingField.IsAttendanceModuleEnabled,
                GeneralSettingField.IsPracticalAutoRejectionEnabled,
                GeneralSettingField.PracticalAutoRejectionDays,
                GeneralSettingField.DailyAttendanceTimeScale
            ])
        },
        {
            initialData: []
        }
    )

    const getOnlineCampusesQuery = useQuery(
        ["online-campuses"],
        async () => {
            return campusesService.getOnlineCampuses()
        },
        {
            initialData: []
        }
    )

    const [
        {
            activeTabKey,
            activeDirectors,
            oldActiveDirectors,
            oldInstructionalCredit,
            oldInstructionalClock,
            oldFameAdvantageEnabled,
            oldFameFreedomEnabled,
            oldIncludeFameIdCheckInErrorLog,
            oldIsAttendanceModuleEnabled,
            oldIsPracticalAutoRejectionEnabled,
            oldPracticalAutoRejectionDays,
            isSubmitting,
            selectedProgressReport,
            oldSelectedProgressReport,
            instructionalCredit,
            instructionalClock,
            fameAdvantageEnabled,
            fameFreedomEnabled,
            includeFameIdCheckInErrorLog,
            isAttendanceModuleEnabled,
            onlineCampuses,
            oldOnlineCampuses,
            isPracticalAutoRejectionEnabled,
            practicalAutoRejectionDays,
            dailyAttendanceTimeScale,
            oldDailyAttendanceTimeScale
        },
        dispatch
    ] = useReducer(reducer, initialState)
    const {t} = useTranslation(["academics", "common"])
    const progressReportOptions = [
        {id: GeneralSettingField.ProgressReportSystemDefault, name: "System default"},
        {id: GeneralSettingField.ProgressReportSubmodulesBreakDown, name: "Submodules breakdown (Clock hour only)"}
    ]

    useEffect(() => {
        if (!generalSettingsQuery.isFetched) return
        const selectedProgressReport = generalSettingsQuery.data
            ?.filter(
                (item) =>
                    item.value === YesNo.Yes &&
                    [
                        GeneralSettingField.ProgressReportSystemDefault,
                        GeneralSettingField.ProgressReportSubmodulesBreakDown
                    ].includes(item.field)
            )
            .map((item) => ({
                id: item.field,
                name:
                    item.field === GeneralSettingField.ProgressReportSystemDefault
                        ? "System default"
                        : "Submodules breakdown (Clock hour only)"
            }))
        const instructionalCredit = generalSettingsQuery.data?.find(
            (item) => item.field === GeneralSettingField.InstructionalCredit
        )
        const instructionalClock = generalSettingsQuery.data?.find(
            (item) => item.field === GeneralSettingField.InstructionalClock
        )
        const fameAdvantageEnabled = generalSettingsQuery.data?.find(
            (item) => item.field === GeneralSettingField.FameAdvantageEnabled
        )
        const fameFreedomEnabled = generalSettingsQuery.data?.find(
            (item) => item.field === GeneralSettingField.FameFreedomEnabled
        )
        const includeFameIdCheckInErrorLog = generalSettingsQuery.data?.find(
            (item) => item.field === GeneralSettingField.IncludeFameIdCheckInErrorLog
        )
        const isAttendanceModuleEnabled = generalSettingsQuery.data?.find(
            (item) => item.field === GeneralSettingField.IsAttendanceModuleEnabled
        )
        const isPracticalAutoRejectionEnabled = generalSettingsQuery.data?.find(
            (item) => item.field === GeneralSettingField.IsPracticalAutoRejectionEnabled
        )
        const practicalAutoRejectionDays = generalSettingsQuery.data?.find(
            (item) => item.field === GeneralSettingField.PracticalAutoRejectionDays
        )
        const dailyAttendanceTimeScale = generalSettingsQuery.data?.find(
            (item) => item.field === GeneralSettingField.DailyAttendanceTimeScale
        )

        dispatch({
            oldSelectedProgressReport: selectedProgressReport,
            selectedProgressReport,
            oldInstructionalCredit: instructionalCredit?.value === YesNo.Yes,
            oldInstructionalClock: instructionalClock?.value === YesNo.Yes,
            oldFameAdvantageEnabled: fameAdvantageEnabled?.value === YesNo.Yes,
            oldFameFreedomEnabled: fameFreedomEnabled?.value === YesNo.Yes,
            oldIncludeFameIdCheckInErrorLog: includeFameIdCheckInErrorLog?.value === YesNo.Yes,
            oldIsAttendanceModuleEnabled: isAttendanceModuleEnabled?.value === YesNo.Yes,
            oldIsPracticalAutoRejectionEnabled: isPracticalAutoRejectionEnabled?.value === YesNo.Yes,
            oldPracticalAutoRejectionDays: practicalAutoRejectionDays?.value,
            oldDailyAttendanceTimeScale: dailyAttendanceTimeScale?.value ?? DailyAttendanceTimeScale.Minutes,
            instructionalCredit: instructionalCredit?.value === YesNo.Yes,
            instructionalClock: instructionalClock?.value === YesNo.Yes,
            fameAdvantageEnabled: fameAdvantageEnabled?.value === YesNo.Yes,
            fameFreedomEnabled: fameFreedomEnabled?.value === YesNo.Yes,
            includeFameIdCheckInErrorLog: includeFameIdCheckInErrorLog?.value === YesNo.Yes,
            isAttendanceModuleEnabled: isAttendanceModuleEnabled?.value === YesNo.Yes,
            isPracticalAutoRejectionEnabled: isPracticalAutoRejectionEnabled?.value === YesNo.Yes,
            practicalAutoRejectionDays: practicalAutoRejectionDays?.value,
            dailyAttendanceTimeScale: dailyAttendanceTimeScale?.value ?? DailyAttendanceTimeScale.Minutes
        })
    }, [generalSettingsQuery.isFetched, generalSettingsQuery.data])

    useEffect(() => {
        if (!getOnlineCampusesQuery.isFetched) return

        const onlineCampuses = getOnlineCampusesQuery.data

        dispatch({
            onlineCampuses,
            oldOnlineCampuses: cloneDeep(onlineCampuses)
        })
    }, [getOnlineCampusesQuery.isFetched, getOnlineCampusesQuery.data])

    useEffect(() => {
        getActiveDirectors()
    }, [])

    async function getActiveDirectors() {
        try {
            const department = allDepartments.find((dept) => dept.departmentId === departmentId)
            if (department) {
                const activeDirectors = department.directors
                dispatch({
                    activeDirectors,
                    oldActiveDirectors: activeDirectors
                })
            }
        } catch (error) {
            handleError(error)
        }
    }

    function onChangeTab(activeKey) {
        dispatch({activeTabKey: activeKey})
    }

    function onChangeAdmissionsDirector(value) {
        dispatch({activeDirectors: value})
    }

    function onClickCancel() {
        dispatch({
            activeDirectors: oldActiveDirectors,
            selectedProgressReport: oldSelectedProgressReport,
            instructionalCredit: oldInstructionalCredit,
            instructionalClock: oldInstructionalClock,
            fameAdvantageEnabled: oldFameAdvantageEnabled,
            fameFreedomEnabled: oldFameFreedomEnabled,
            includeFameIdCheckInErrorLog: oldIncludeFameIdCheckInErrorLog,
            isAttendanceModuleEnabled: oldIsAttendanceModuleEnabled,
            isPracticalAutoRejectionEnabled: oldIsPracticalAutoRejectionEnabled,
            practicalAutoRejectionDays: oldPracticalAutoRejectionDays,
            onlineCampuses: oldOnlineCampuses,
            dailyAttendanceTimeScale: oldDailyAttendanceTimeScale
        })
    }

    async function onClickSave() {
        try {
            dispatch({isSubmitting: true})
            const profileIds = activeDirectors.map((item) => item.profileId)
            const progressReportSystemDefaultValue = !!selectedProgressReport?.find(
                (item) => item.id === GeneralSettingField.ProgressReportSystemDefault
            )
            const progressReportSubmoduleBreakDownValue = !!selectedProgressReport?.find(
                (item) => item.id === GeneralSettingField.ProgressReportSubmodulesBreakDown
            )
            await Promise.all([
                departmentsService.edit({
                    departmentId,
                    directorProfileIds: profileIds
                }),
                updateGeneralSettings.mutate([
                    {
                        field: GeneralSettingField.ProgressReportSystemDefault,
                        checked: progressReportSystemDefaultValue
                    },
                    {
                        field: GeneralSettingField.ProgressReportSubmodulesBreakDown,
                        checked: progressReportSubmoduleBreakDownValue
                    },
                    {
                        field: GeneralSettingField.InstructionalCredit,
                        checked: instructionalCredit
                    },
                    {
                        field: GeneralSettingField.InstructionalClock,
                        checked: instructionalClock
                    },
                    {
                        field: GeneralSettingField.FameAdvantageEnabled,
                        checked: fameAdvantageEnabled
                    },
                    {
                        field: GeneralSettingField.FameFreedomEnabled,
                        checked: fameFreedomEnabled
                    },
                    {
                        field: GeneralSettingField.IncludeFameIdCheckInErrorLog,
                        checked: includeFameIdCheckInErrorLog
                    },
                    {
                        field: GeneralSettingField.IsAttendanceModuleEnabled,
                        checked: isAttendanceModuleEnabled
                    },
                    {
                        field: GeneralSettingField.IsPracticalAutoRejectionEnabled,
                        checked: isPracticalAutoRejectionEnabled
                    },
                    {
                        field: GeneralSettingField.PracticalAutoRejectionDays,
                        value: practicalAutoRejectionDays
                    },
                    {
                        field: GeneralSettingField.DailyAttendanceTimeScale,
                        value: dailyAttendanceTimeScale
                    }
                ]),
                canEditCampuses
                    ? campusesService.updateOnlineCampuses({
                          campusIds: onlineCampuses?.map((campus) => campus.id) ?? []
                      })
                    : null
            ])

            await Promise.all([getActiveDirectors(), generalSettingsQuery.refetch(), getOnlineCampusesQuery.refetch()])
            invalidateDepartmentsCache()
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isSubmitting: false})
        }
    }

    const loadOptions = async (input, loadedOptions) => {
        try {
            const pageSize = 20
            const page = Math.ceil(loadedOptions.length / pageSize) + 1
            const params = {
                range: {
                    page,
                    pageSize
                }
            }
            const {data: directors, total} = await departmentsService.searchDirectors(input, params)
            return {
                options: directors,
                hasMore: loadedOptions.length < total
            }
        } catch (error) {
            return {
                options: [],
                hasMore: false
            }
        }
    }

    const getDirectorTitle = () => {
        switch (departmentId) {
            case BaseDepartmentId.Academics:
                return "Academics Director"
            case BaseDepartmentId.Admissions:
                return t("settings.director")
            case BaseDepartmentId.Alumni:
                return "Alumni Director"
            case BaseDepartmentId.CareerServices:
                return "Career Service Director"
            case BaseDepartmentId.FinancialAid:
                return "Financial Aid Director"
            case BaseDepartmentId.StudentAccount:
                return "Student Account Director"
            case BaseDepartmentId.StudentServices:
                return "Student Service Director"
            default:
                break
        }
    }

    const debounceUsers = debounce(loadOptions, 500)
    const isChanged =
        !isEqual(activeDirectors, oldActiveDirectors) ||
        !isEqual(selectedProgressReport, oldSelectedProgressReport) ||
        !isEqual(instructionalCredit, oldInstructionalCredit) ||
        !isEqual(instructionalClock, oldInstructionalClock) ||
        !isEqual(fameAdvantageEnabled, oldFameAdvantageEnabled) ||
        !isEqual(fameFreedomEnabled, oldFameFreedomEnabled) ||
        !isEqual(includeFameIdCheckInErrorLog, oldIncludeFameIdCheckInErrorLog) ||
        !isEqual(isAttendanceModuleEnabled, oldIsAttendanceModuleEnabled) ||
        !isEqual(isPracticalAutoRejectionEnabled, oldIsPracticalAutoRejectionEnabled) ||
        !isEqual(practicalAutoRejectionDays, oldPracticalAutoRejectionDays) ||
        !isEqual(onlineCampuses, oldOnlineCampuses) ||
        !isEqual(dailyAttendanceTimeScale, oldDailyAttendanceTimeScale)

    return (
        <div className={styles.wrapper}>
            <div className={styles.settingWrap}>
                <Row gutter={[24, 24]}>
                    <Col span={12}>
                        <div className={styles.fieldWrap}>
                            <FormLabel label={getDirectorTitle()} />
                            <KlassDropAsyncPaginate
                                value={activeDirectors}
                                valueKey="profileId"
                                onChange={onChangeAdmissionsDirector}
                                getOptionLabel={(option: any) => getFullName(option)}
                                loadOptions={debounceUsers}
                                isMulti
                                placeholder="Director"
                            />
                        </div>
                    </Col>
                    <Col span={12}>
                        <div className={styles.fieldWrap}>
                            <FormLabel label="Progress Report / Transcript" />
                            <KlassDropdown
                                options={progressReportOptions}
                                value={selectedProgressReport}
                                isMulti
                                onChange={(newValue) => dispatch({selectedProgressReport: newValue})}
                            />
                        </div>
                    </Col>
                </Row>

                <Row gutter={[24, 24]}>
                    <Col span={12}>
                        <div className={styles.fieldWrap}>
                            <FormLabel label="Instructional Tab" />
                            <Checkbox
                                checked={instructionalCredit}
                                onChange={(event) => dispatch({instructionalCredit: event.target.checked})}>
                                Instructional Credit
                            </Checkbox>
                            <Checkbox
                                checked={instructionalClock}
                                onChange={(event) => dispatch({instructionalClock: event.target.checked})}>
                                Instructional Clock
                            </Checkbox>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div className={styles.fieldWrap}>
                            <FormLabel label="Online Campuses" />
                            <CampusSelect
                                isMulti
                                isClearable
                                placeholder="Campuses"
                                isDisabled={!canEditCampuses}
                                value={onlineCampuses}
                                onChange={(newValue) => {
                                    dispatch({onlineCampuses: newValue})
                                }}
                            />
                        </div>
                    </Col>
                </Row>

                <Row gutter={[24, 24]} style={{marginTop: 16}}>
                    <Col span={12}>
                        <div className={styles.fieldWrap}>
                            <FormLabel label="Is Attendance Module Enabled" />
                            <Checkbox
                                checked={isAttendanceModuleEnabled}
                                onChange={(event) => dispatch({isAttendanceModuleEnabled: event.target.checked})}>
                                Enabled
                            </Checkbox>
                        </div>
                        <div className={cx(styles.fieldWrap, styles.practicalAutoRejectionItems)}>
                            <div>
                                <FormLabel label="Practical Auto Rejection" />
                                <Checkbox
                                    checked={isPracticalAutoRejectionEnabled}
                                    onChange={(event) =>
                                        dispatch({isPracticalAutoRejectionEnabled: event.target.checked})
                                    }>
                                    Enabled
                                </Checkbox>
                            </div>

                            {!!isPracticalAutoRejectionEnabled && (
                                <div>
                                    <FormLabel label="Days" />
                                    <BaseInput
                                        onChange={(text) => {
                                            dispatch({practicalAutoRejectionDays: text})
                                        }}
                                        placeholder={"Days"}
                                        style={{width: 100}}
                                        disabledArrow
                                        type="number"
                                        min={0}
                                        value={practicalAutoRejectionDays}
                                    />
                                </div>
                            )}
                        </div>
                        <div className={styles.fieldWrap}>
                            <FormLabel label="Daily Attendance Time Scale" />
                            <div className={styles.timeScaleRadio}>
                                <RadioButton
                                    text="Minutes"
                                    value={DailyAttendanceTimeScale.Minutes}
                                    checked={dailyAttendanceTimeScale === DailyAttendanceTimeScale.Minutes}
                                    onClick={() =>
                                        dispatch({dailyAttendanceTimeScale: DailyAttendanceTimeScale.Minutes})
                                    }
                                />
                                <RadioButton
                                    text="Hours"
                                    value={DailyAttendanceTimeScale.Hours}
                                    checked={dailyAttendanceTimeScale === DailyAttendanceTimeScale.Hours}
                                    onClick={() => dispatch({dailyAttendanceTimeScale: DailyAttendanceTimeScale.Hours})}
                                />
                            </div>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div className={styles.fieldWrap}>
                            <FormLabel label="Enable Fame" />
                            <Checkbox
                                checked={fameAdvantageEnabled || fameFreedomEnabled}
                                onChange={(event) => {
                                    if (event.target.checked) {
                                        dispatch({fameAdvantageEnabled: true, fameFreedomEnabled: false})
                                    } else {
                                        dispatch({fameAdvantageEnabled: false, fameFreedomEnabled: false})
                                    }
                                }}>
                                Enabled
                            </Checkbox>
                        </div>

                        {fameAdvantageEnabled || fameFreedomEnabled ? (
                            <div className={styles.radioWrapper}>
                                <div className={styles.radioItem}>
                                    <RadioButton
                                        text={"Fame Advantage"}
                                        value="advantage"
                                        checked={fameAdvantageEnabled}
                                        onClick={() =>
                                            dispatch({fameAdvantageEnabled: true, fameFreedomEnabled: false})
                                        }
                                    />
                                    <p className={styles.description}>
                                        (Post when punches are approved (Have check-in and check-out)
                                    </p>
                                </div>
                                <div className={styles.radioItem}>
                                    <RadioButton
                                        text="Fame Freedom / Advantage"
                                        value="freedom"
                                        checked={fameFreedomEnabled}
                                        onClick={() =>
                                            dispatch({fameFreedomEnabled: true, fameAdvantageEnabled: false})
                                        }
                                    />
                                    <p className={styles.description}>(Posts when punches are created)</p>
                                </div>
                                {fameFreedomEnabled ? (
                                    <div>
                                        <FormLabel label="" />
                                        <Checkbox
                                            checked={includeFameIdCheckInErrorLog}
                                            onChange={(event) =>
                                                dispatch({includeFameIdCheckInErrorLog: event.target.checked})
                                            }>
                                            Include the Fame ID check in the error log.
                                        </Checkbox>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </Col>
                </Row>

                <PermissionsRequired permissions={{staff: [Permissions.Staff.Settings.Modules.Academics.Edit]}}>
                    {isChanged && (
                        <div className={styles.buttonWrap}>
                            <SecondaryButton
                                title={t("common:action.cancel")}
                                onClick={onClickCancel}
                                className={styles.cancelBtn}
                            />
                            <BaseButton
                                title={t("common:action.save").toUpperCase()}
                                onClick={onClickSave}
                                loading={isSubmitting}
                            />
                        </div>
                    )}
                </PermissionsRequired>
            </div>
            <Tabs className="klassSubTab" activeKey={activeTabKey} onChange={onChangeTab}>
                {isModuleEnable(Modules.ActivitiesAndFollowups) && (
                    <TabPane tab={<TabHeader title={t("settings.activities")} />} key={SettingsTab.Activities}>
                        <Activities
                            {...{
                                departmentId,
                                model: props.model,
                                invalidateActivitiesCache
                            }}
                        />
                    </TabPane>
                )}
                {departmentId === BaseDepartmentId.Academics && (
                    <>
                        <TabPane
                            tab={<TabHeader title={t("settings.academicTrack")} />}
                            key={SettingsTab.AcademicTrack}>
                            <AcademicTrack model={props.model} />
                        </TabPane>
                        <TabPane
                            tab={<TabHeader title={t("settings.dailyAttendance")} />}
                            key={SettingsTab.DailyAttendance}>
                            {activeTabKey === SettingsTab.DailyAttendance ? <DailyAttendance /> : null}
                        </TabPane>
                        <TabPane
                            tab={<TabHeader title={t("settings.classroomAttendance")} />}
                            key={SettingsTab.ClassroomAttendance}>
                            {activeTabKey === SettingsTab.ClassroomAttendance ? <ClassroomAttendance /> : null}
                        </TabPane>
                        {isModuleEnable(Modules.AcademicLocationTrackingAttendance) ? (
                            <TabPane tab={<TabHeader title={t("settings.geofencing")} />} key={SettingsTab.GeoFencing}>
                                {activeTabKey === SettingsTab.GeoFencing ? <GeofencingSettings /> : null}
                            </TabPane>
                        ) : null}
                        <TabPane
                            tab={<TabHeader title={t("settings.integrationMapping")} />}
                            key={SettingsTab.IntegrationMapping}>
                            <IntegrationMapping model={props.model} />
                        </TabPane>
                    </>
                )}
                <TabPane tab={<TabHeader title={t("settings.phases")} />} key={SettingsTab.Phases}>
                    {activeTabKey === SettingsTab.Phases ? <Phase model={props.model} /> : null}
                </TabPane>
                <TabPane tab={<TabHeader title={t("settings.shifts")} />} key={SettingsTab.Shifts}>
                    {activeTabKey === SettingsTab.Shifts ? <Shift model={props.model} /> : null}
                </TabPane>
                <TabPane
                    tab={<TabHeader title={t("settings.clockHoursTranscript")} />}
                    key={SettingsTab.ClockHoursTranscript}>
                    <ClockHoursTranscript />
                </TabPane>
            </Tabs>
        </div>
    )
}
